/**
 * ⚠️ LEGACY — superseded by src/features/dashboards/api/client.ts (Wave 3, 2026-05-18).
 *
 * The legacy AdminModernDashboard page (src/pages/Dashboard/AdminModernDashboard.tsx)
 * still consumes this module — both will be deleted together after the
 * 2-sprint cooling period defined in MIGRATION_PLAYBOOK.md §2 Step 10.
 *
 * DO NOT add features or fixes here. All new dashboard work happens in
 * src/features/dashboards/ (TanStack Query + typed client).
 */
import { APIClient } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";

const api = new APIClient();

/**
 * Dashboard API Response Types
 * These match the structure defined in ADMIN_DASHBOARD_API_ANALYSIS.md
 */

export interface DashboardKPIs {
  activeScaffolds: number;
  activeWorkOrders: number;
  quotesPending: number;
  totalQuotes: number;
  inventoryItems: number;
  lowStockItems: number;
}

export interface ScaffoldStatusBreakdown {
  planned: number;
  inProgress: number;
  draft: number;
}

export interface DashboardWorkOrder {
  id: string;
  workOrderNumber: string;
  clientName: string;
  status: string | number; // API returns string (e.g., "Planned", "In Progress") or number
  created: string; // ISO date-time string
}

export interface DashboardQuote {
  id: string;
  quoteNumber: string;
  clientName: string;
  status: string | number; // API returns string (e.g., "Under Review") or number
  totalAmount: number;
  total?: number; // Alternative field name
}

/**
 * Helper function to normalize work order status to number
 * Handles both string and number statuses from API
 */
export const normalizeWorkOrderStatus = (status: string | number): number => {
  if (typeof status === 'number') return status;
  
  // Map string statuses to enum values
  const statusMap: Record<string, number> = {
    'Draft': 1,
    'Planned': 2,
    'In Progress': 3,
    'InProgress': 3,
    'Closed': 4,
  };
  
  return statusMap[status] || parseInt(status as string, 10) || 1;
};

/**
 * Helper function to normalize quote status to number
 * Handles both string and number statuses from API
 */
export const normalizeQuoteStatus = (status: string | number): number => {
  if (typeof status === 'number') return status;
  
  // Map string statuses to enum values
  const statusMap: Record<string, number> = {
    'Draft': 1,
    'Under Review': 2,
    'UnderReview': 2,
    'Approved': 3,
    'Sent': 4,
    'Change Requested': 5,
    'ChangeRequested': 5,
    'Awarded': 6,
    'Rejected': 7,
    'Expired': 8,
    'WorkOrderGenerated': 9,
  };
  
  return statusMap[status] || parseInt(status as string, 10) || 1;
};

/**
 * Helper function to normalize meeting status to number
 * Handles both string and number statuses from API
 */
export const normalizeMeetingStatus = (status: string | number | undefined): number | undefined => {
  if (status === undefined || status === null) return undefined;
  if (typeof status === 'number') return status;
  
  // Map string statuses to enum values
  const statusMap: Record<string, number> = {
    'Scheduled': 1,
    'Completed': 2,
    'Cancelled': 3,
    'Rescheduled': 4,
    '1': 1,
    '2': 2,
    '3': 3,
    '4': 4,
  };
  
  return statusMap[status] || parseInt(status as string, 10) || undefined;
};

export interface DashboardMeeting {
  id: string;
  meetingTitle: string;
  clientName: string;
  meetingLocation?: string;
  meetingDate: string; // ISO date-time string (API returns full datetime)
  meetingStartTime: string; // Time string (HH:mm:ss)
  meetingStatus?: number | string; // Meeting status enum value (can be number or string from API)
}

export interface InventoryHealth {
  totalItems: number;
  lowStockItems: number;
  healthScore?: string; // "Excellent" | "Good" | "Attention Needed"
}

export interface SystemAlert {
  id: number;
  title: string;
  time: string; // Relative time string (e.g., "2h ago")
  type: "success" | "warning" | "info" | "error";
}

export interface AdminDashboardResponse {
  kpIs?: DashboardKPIs; // API returns "kpIs" (capital I)
  kpis?: DashboardKPIs; // Fallback for lowercase version
  scaffoldStatusBreakdown: ScaffoldStatusBreakdown;
  recentWorkOrders: DashboardWorkOrder[];
  pendingQuotes: DashboardQuote[];
  upcomingMeetings: DashboardMeeting[];
  inventoryHealth: InventoryHealth;
  systemAlerts?: SystemAlert[]; // Optional, can be derived
  lastUpdated?: string; // ISO date-time string
}

/**
 * Get Admin Dashboard data
 * @param limit - Optional limit for recent items (default: 5 for work orders/quotes, 4 for meetings)
 * @returns Promise with admin dashboard response
 * 
 * Note: Upcoming meetings are filtered to show top 4 from today onwards (no date range limit)
 */
export const getAdminDashboard = (
  limit?: number
): Promise<AdminDashboardResponse> => {
  const params: Record<string, string | number> = {};
  
  if (limit) {
    params.limit = limit;
  }
  
  return api.get<AdminDashboardResponse>(url.GET_DASHBOARD_ADMIN, params);
};

